import React, { useEffect, useMemo, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import { connect, getIn } from "formik";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Popper from "@mui/material/Popper";
import Avatar from '@mui/material/Avatar';
import "../../../../pages/admin/admin.css";
import { Chip, FormHelperText, FormControl, Box, Typography, Tooltip, Button } from "@mui/material";
import CustomizedCheckbox from "../CustomCheckBox";
import { stringAvatar } from "../Avatar";

const Autocompletes = connect(
  ({
    Datas,
    PlaceHolder,
    formik,
    name,
    optionLabelName,
    optionId,
    mode,
    getOptionDisabled,
    ...additionalprops
  }) => {

    const listRef = useRef(null);
    const [limit, setLimit] = useState(10);
    const [inputValue, setInputValue] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    const handleScroll = () => {
      setLimit(prev => prev + 5);
    };

    // Don't reset limit on close
    const handleClose = () => { };

    const PopperMy = useMemo(
      () => (props) => {
        return (
          <Popper {...props} sx={{
            paddingTop: "8px", paddingBottom: "8px", color: "#212121",
            "& .MuiAutocomplete-listbox": {
              "& li": {
                paddingLeft: mode === "Edit" ? "0px" : "8px",
                maxWidth: "100% !important",
                wordBreak: "break-all !important",
                whiteSpace: "normal !important",
                textOverflow: "inherit !important"
              },
              "& .MuiAutocomplete-option[aria-selected='true']": {
                backgroundColor: "transparent !important",
              },
            },
            '& .MuiAutocomplete-paper': {
              boxShadow: 'none',
              border: "1px solid #E5E5E5",
              borderRadius: mode === "Edit" ? '0px' : "8px",
              borderBottom: mode === "Edit" ? '0px' : "1px solid #E5E5E5",
              borderRight: mode === "Edit" ? '0px' : "1px solid #E5E5E5",
              borderLeft: mode === "Edit" ? '0px' : "1px solid #E5E5E5",
              borderTop: mode === "Edit" ? "0px" : "1px solid #E5E5E5",
              height: mode === "Edit" ? "290px" : "",
            }, "& .MuiAutocomplete-noOptions": {
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#212121"
            }
          }} />
        );
      },
      [],
    );

    const selected = getIn(formik?.values, name);

    // Calculate initial selectAll state based on enabled options only
    const getInitialSelectAllState = () => {
      try {
        if (!selected || !Datas || selected?.length === 0 || Datas?.length === 0) return false;

        const enabledTotalItems = Datas.filter(item => {
          if (getOptionDisabled && getOptionDisabled(item)) {
            return false;
          }
          return true;
        });

        const enabledSelectedCount = selected.filter(item => {
          if (getOptionDisabled && getOptionDisabled(item)) {
            return false;
          }
          return true;
        }).length;

        return enabledSelectedCount === enabledTotalItems.length && enabledTotalItems.length > 0;
      } catch (error) {
        console.log(error);
        return selected?.length === Datas?.length;
      }
    };

    let [selectAll, setSelectAll] = useState(getInitialSelectAllState());
    const error = getIn(formik.errors, name);

    const getTouchedStatus = () => {
      let touched = getIn(formik.touched, name);
      if (touched) {
        return touched.length === 0
      } else {
        return touched
      }
    }

    useEffect(() => {
      if (selected?.length === 0) {
        setSelectAll(false);
      } else {
        try {
          // Update selectAll state based on enabled options
          const enabledTotalItems = Datas.filter(item => {
            if (getOptionDisabled && getOptionDisabled(item)) {
              return false;
            }
            return true;
          });

          const enabledSelectedCount = selected.filter(item => {
            if (getOptionDisabled && getOptionDisabled(item)) {
              return false;
            }
            return true;
          }).length;

          setSelectAll(enabledSelectedCount === enabledTotalItems.length && enabledTotalItems.length > 0);
        } catch (error) {
          console.log(error);
          setSelectAll(selected?.length === Datas?.length);
        }
      }
    }, [selected, Datas]);

    useEffect(() => {
      // Update filtered data whenever the input value changes
      if (inputValue) {
        const filtered = Datas.filter(item =>
          item[optionLabelName].toLowerCase().includes(inputValue.toLowerCase())
        );
        setFilteredData(filtered);
      } else {
        setFilteredData(Datas);
      }
    }, [inputValue, Datas, optionLabelName]);

    const touch = getTouchedStatus();

    // Function to handle select all with event prevention
    const handleSelectAll = (e) => {
      // Make sure we stop event propagation
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      const filteredItems = inputValue
        ? Datas.filter(item => item[optionLabelName].toLowerCase().includes(inputValue.toLowerCase()))
        : Datas;

      // Filter out disabled options when selecting all
      const enabledFilteredItems = filteredItems.filter(item => {
        try {
          // Check if option is disabled using getOptionDisabled prop
          if (getOptionDisabled && getOptionDisabled(item)) {
            return false;
          }
          return true;
        } catch (error) {
          console.log(error);
          return true;
        }
      });

      // Create a map of existing selected items by their ID for efficient lookup
      const existingSelectedMap = new Map(
        (selected || []).map(item => [item[optionId || 'id'], item])
      );

      // Add only enabled filtered items to the map (this will override duplicates, keeping the selection)
      enabledFilteredItems.forEach(item => {
        existingSelectedMap.set(item[optionId || 'id'], item);
      });

      // Convert map back to array
      const newSelection = Array.from(existingSelectedMap.values());

      formik.setFieldValue(name, newSelection);
      try {
        // Calculate selectAll state based on enabled options only
        const enabledTotalItems = Datas.filter(item => {
          if (getOptionDisabled && getOptionDisabled(item)) {
            return false;
          }
          return true;
        });
        setSelectAll(enabledFilteredItems.length === enabledTotalItems.length);
      } catch (error) {
        console.log(error);
        setSelectAll(filteredItems.length === Datas.length);
      }
    };

    // Function to handle clear filtered items with event prevention
    const handleClearFiltered = (e) => {
      // Make sure we stop event propagation
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // If no filter or no selected items, do nothing
      if (!selected || selected.length === 0) return;

      // Get the current filtered items that match the search text
      const filtered = Datas.filter(item =>
        item[optionLabelName].toLowerCase().includes(inputValue.toLowerCase())
      );

      // If no items match the filter, do nothing
      if (filtered.length === 0) return;

      // Create a set of IDs for faster lookup
      const filteredIds = new Set(filtered.map(item => item.id));

      // Remove matching items from selection (keep only non-filtered items)
      const newSelected = selected.filter(item => !filteredIds.has(item.id));

      // Update the form with the new selection
      formik.setFieldValue(name, newSelected);

      // Update other state as needed
      setSelectAll(false);
    };

    // Very simplified filter logic - this should avoid performance issues
    const simplifiedFilter = createFilterOptions({
      matchFrom: 'any',
      limit: limit,
      ignoreCase: true,
    });

    return (
      <>
        {mode === "Edit" &&
          <Box sx={{
            border: 1,
            borderRadius: "8px",
            paddingTop: "8px",
            paddingLeft: "15px",
            paddingBottom: "8px",
            borderColor: "#D6D6D7",
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            marginTop: "-30px",
          }}
          >
            <label
              sx={{
                color: "#212121",
                fontWeight: 400,
                fontSize: "14px",
                marginLeft: "10px",
              }}
            >
              {selected?.length} Selected...
            </label>
          </Box>
        }
        <Box
          sx={(mode === "Edit") ? {
            border: 1,
            borderRadius: "8px",
            paddingTop: "15px",
            paddingLeft: "15px",
            paddingBottom: "305px",
            borderColor: "#D6D6D7",
            borderTop: 0,
            paddingRight: "15px",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          } : {}}
        >
          <Autocomplete
            sx={{
              "& .css-5dy5t1-MuiInputBase-root-MuiOutlinedInput-root": {
                borderRadius: "8px",
                minHeight: "44px",
                fontSize: "14px",
                borderColor: "#E6E5E6"
              },
              '& .MuiAutocomplete-listbox': {
                scrollbarColor: 'rgb(190, 190, 190) rgb(240, 240, 240)',
                scrollbarWidth: 'thin',
                borderRadius: "8px"
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: "8px",
                minHeight: "44px",
                fontSize: "14px",
                borderColor: "#E6E5E6"
              }
            }}
            className="ozcw-scrollbar"
            noOptionsText={"No matching result found!"}
            multiple={true}
            size="small"
            id={name}
            options={Datas}
            value={selected}
            inputValue={inputValue}
            selectOnFocus={false}
            disableCloseOnSelect={true}
            autoComplete={true}
            getOptionDisabled={(option) => {
              if (getOptionDisabled) {
                return getOptionDisabled(option);
              }
              return false;
            }}

            renderTags={(value) => (
              <div style={{
                display: "flex",
                alignItems: 'center',
                alignContent: "center",
              }}>
                <Typography component={'div'}>
                  {selected?.map(
                    (selectedValues, index) =>
                    (index < 3 && selectedValues[optionLabelName] !== "ACTION_BUTTONS_ROW" && (
                      <Chip
                        key={`${selectedValues[optionLabelName]}-${index}`}
                        label={selectedValues[optionLabelName]}
                        size="small"
                        className="oz-customAutoComplete-chip-style"
                      />
                    ))
                  )}
                </Typography>
                <Typography sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#212121",
                  paddingRight: '5px',
                  paddingLeft: "5px"
                }}>
                  {(selected?.length > 3) ? "..." : ""}
                </Typography>
                <Typography sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#99A0A8",
                }}>
                  {(selected?.length > 3) ? (("+" + (selected?.length - 3))) : ""}
                </Typography>
              </div>
            )}

            onChange={(e, value, reason, details) => {
              // Skip the ACTION_BUTTONS_ROW from being selected
              const filteredValue = value.filter(option => option[optionLabelName] !== "ACTION_BUTTONS_ROW");

              // Set the filtered value directly without special handling for legacy Select All
              formik.setFieldValue(name, filteredValue);
            }}

            getOptionLabel={option => option[optionLabelName]}
            PopperComponent={PopperMy}

            isOptionEqualToValue={(option, value) =>
              option[optionId || optionLabelName] === value[optionId || optionLabelName]
            }

            ListboxProps={{
              ref: listRef,
              onScroll: handleScroll,
              sx: { maxHeight: "280px" }
            }}

            onClose={handleClose}

            onInputChange={(e, newValue, reason) => {
              if (reason === 'input') {
                setInputValue(newValue);
              }
            }}

            filterOptions={(options, params) => {
              // Basic filtering
              let filtered = simplifiedFilter(options, params);

              // Add action buttons as the first option
              if (filtered.length > 0) {
                filtered.unshift({
                  id: "actionButtons",
                  [optionLabelName]: "ACTION_BUTTONS_ROW"
                });
              }

              return filtered;
            }}

            renderOption={(props, option, { selected }) => {
              // Render the action buttons row with the exact same styling as in the attached component
              if (option[optionLabelName] === "ACTION_BUTTONS_ROW") {
                return (
                  <li {...props}
                    style={{
                      padding: 2,
                      display: 'flex',
                      flexDirection: 'row',
                      borderBottom: '1px solid #E5E5E5',
                      height: 'auto !important',

                    }}
                    // These event handlers are critical to prevent the dropdown from closing
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Button
                      sx={{
                        textTransform: 'none',
                        flex: 1,
                        justifyContent: 'center',
                        fontFamily: 'Roboto',
                        fontWeight: 400,
                        color: '#3D8BF8',
                        borderRight: '1px solid #E5E5E5',
                        minWidth: 'auto',
                        borderRadius: 0,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectAll(e);
                        return false;
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      Select All
                    </Button>
                    <Button
                      style={{
                        textTransform: 'none',
                        flex: 1,
                        justifyContent: 'center',
                        fontFamily: 'Roboto',
                        fontWeight: 400,
                        color: '#3D8BF8',
                        minWidth: 'auto',
                        borderRadius: 0,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearFiltered(e);
                        return false;
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {"Clear All"}
                    </Button>
                  </li>
                );
              }

              // Render normal options
              return (
                <li {...props} key={`${option[optionLabelName]}-${option.id}`}>
                  <div style={{ display: 'flex', alignItems: "center" }}>
                    <CustomizedCheckbox
                      checked={selected}
                    />
                    <Avatar
                      style={{
                        width: "24px",
                        height: "24px",
                        fontSize: "14px",
                        color: "#212121",
                        marginLeft: "4px"
                      }}
                      {...stringAvatar(option[optionLabelName])}
                    />
                    <span style={{
                      marginLeft: "8px",
                      color: "#212121",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "16.41px"
                    }}>
                      {option[optionLabelName]}
                    </span>
                  </div>
                </li>
              );
            }}

            renderInput={(params) => (
              <div>
                <FormControl
                  component="fieldset"
                  error={touch & Boolean(error) ? true : false}
                  fullWidth
                >
                  <TextField
                    error={touch & Boolean(error) ? true : false}
                    {...params}
                    placeholder={PlaceHolder}
                  />
                  <FormHelperText>
                    {touch & Boolean(error) ? error : null}
                  </FormHelperText>
                </FormControl>
              </div>
            )}
          />
        </Box>
      </>
    );
  }
);

export default Autocompletes;